import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function AssessmentForm({ showToast }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { authFetch } = useAuth();
  const pageData  = location.state || {};

  // companyId + name can come from navigation state OR be selected on-page
  const [companies,   setCompanies]   = useState([]);
  const [selId,       setSelId]       = useState(pageData.companyId || '');
  const [selName,     setSelName]     = useState(pageData.companyName || '');
  const [emailInput,  setEmailInput]  = useState('');
  const [sending,     setSending]     = useState(false);
  const [sentCount,   setSentCount]   = useState(0);
  const [tab,         setTab]         = useState('link');

  // Load company list only when no company was passed via state
  useEffect(() => {
    if (!pageData.companyId) {
      authFetch('/api/companies').then(r => r.json()).then(d => {
        if (Array.isArray(d)) setCompanies(d);
      }).catch(() => {});
    }
  }, []);

  const companyId   = selId;
  const companyName = selName;
  const link        = companyId ? `${window.location.origin}/assess/${companyId}` : '';

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    showToast('Assessment link copied!', 'info');
  };

  const sendReminders = async () => {
    const emails = emailInput.split(/[\n,;]+/).map(e => e.trim()).filter(e => e.includes('@'));
    if (!emails.length) { showToast('Enter at least one valid email', 'error'); return; }
    if (emails.length > 50) { showToast('Max 50 emails per batch', 'error'); return; }
    setSending(true);
    const res  = await authFetch(`/api/companies/${companyId}/send-reminder`, {
      method: 'POST', body: JSON.stringify({ emails }),
    });
    const data = await res.json();
    if (res.ok) {
      setSentCount(data.sent);
      showToast(`${data.sent} reminder${data.sent !== 1 ? 's' : ''} sent!`);
      setEmailInput('');
    } else showToast(data.error || 'Failed to send', 'error');
    setSending(false);
  };

  // ── No company selected yet ────────────────────────────────────────────────
  if (!companyId) return (
    <div>
      <div className="page-header">
        <div className="page-title">Share Assessment</div>
        <div className="page-sub">Select a company to get its shareable assessment link</div>
      </div>

      {companies.length > 0 ? (
        <div className="card" style={{ maxWidth: 480 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
          <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Select a Company</div>
          <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 20 }}>
            Choose which company's assessment link you want to share.
          </div>
          <select className="form-select" style={{ marginBottom: 14 }}
            value={selId}
            onChange={e => {
              const c = companies.find(x => x.id === e.target.value);
              setSelId(e.target.value);
              setSelName(c?.name || '');
            }}>
            <option value="">Choose company…</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      ) : (
        <div className="card empty-state">
          <div className="empty-icon">🔗</div>
          <div className="empty-title">Select a company first</div>
          <div className="empty-sub">Go to Companies and click "Share Link" on any company</div>
          <button className="btn btn-primary" onClick={() => navigate('/companies')}>Go to Companies</button>
        </div>
      )}
    </div>
  );

  // ── Company selected — show share tools ────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div className="page-title">Share Assessment — {companyName}</div>
        <div className="page-sub">Share the link or send email reminders directly from here</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[{ k: 'link', label: '🔗 Share Link' }, { k: 'reminder', label: '📧 Send Reminders' }].map(t => (
          <button key={t.k} className={`btn ${tab === t.k ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.k)}>{t.label}</button>
        ))}
      </div>

      {/* Share Link tab */}
      {tab === 'link' && (
        <>
          <div className="card card-accent" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 24, marginBottom: 12 }}>🔗</div>
            <div style={{ fontFamily: 'var(--ff-d)', fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
              {companyName}
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
              Anyone with this link can complete the assessment. No account required.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{
                flex: 1, background: 'var(--bg3)', border: '1px solid var(--border2)',
                borderRadius: 'var(--r)', padding: '10px 14px', fontFamily: 'var(--ff-m)',
                fontSize: 12, color: 'var(--text2)', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {link}
              </div>
              <button className="btn btn-primary" onClick={copy}>📋 Copy Link</button>
            </div>
          </div>

          <div className="g3">
            {[
              { icon: '📬', title: 'Email it',        body: 'Send to leadership team, all managers, and a sample of individual contributors. Aim for a cross-section.' },
              { icon: '💬', title: 'WhatsApp / Slack', body: 'Drop the link in your company group chat with a short note asking everyone to complete it this week.' },
              { icon: '🎯', title: 'Who to include',  body: 'Aim for 15–30 people: 5–8 Leadership, 8–12 Managers, 5–10 Individual Contributors.' },
            ].map(t => (
              <div key={t.title} className="card">
                <div style={{ fontSize: 24, marginBottom: 10 }}>{t.icon}</div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{t.title}</div>
                <div style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.6 }}>{t.body}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Send Reminders tab */}
      {tab === 'reminder' && (
        <div className="card" style={{ maxWidth: 560 }}>
          <div className="card-title">Send Email Reminders</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Enter email addresses (one per line, or comma-separated). Each person receives a direct link to the assessment.
          </div>
          <div className="form-group">
            <label className="form-label">Email Addresses (max 50)</label>
            <textarea className="form-textarea"
              style={{ minHeight: 120, fontFamily: 'var(--ff-m)', fontSize: 12 }}
              placeholder={'rajesh@company.com\npriya@company.com\nsuresh@company.com'}
              value={emailInput} onChange={e => setEmailInput(e.target.value)} />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
              {emailInput.split(/[\n,;]+/).filter(e => e.trim().includes('@')).length} valid emails detected
            </div>
          </div>
          {sentCount > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.08)', borderRadius: 8, border: '1px solid rgba(34,197,94,0.2)', marginBottom: 14 }}>
              <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>✓ {sentCount} reminder{sentCount !== 1 ? 's' : ''} sent</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" disabled={sending || !emailInput.trim()} onClick={sendReminders}>
              {sending ? 'Sending…' : '📧 Send Reminders'}
            </button>
            <button className="btn btn-ghost" onClick={() => navigate('/respondents', { state: { companyId } })}>
              View Responses →
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
        <button className="btn btn-ghost" onClick={() => navigate('/companies')}>← Companies</button>
        <button className="btn btn-primary" onClick={() => navigate('/report', { state: { companyId } })}>
          View Report →
        </button>
      </div>
    </div>
  );
}
